<script lang="ts" setup>
import { scrollTo } from '@/utils/helpers'
interface Props {
  navbarColor?: string
  layoutButtonText: string
  isTransparent?: boolean
  buttonsColor?: '#000033' | '#fff'
}
const props = defineProps<Props>()

const localePath = useLocalePath()

const navStore = useNavStore()

const buttonLink = computed(() => {
  return navStore.simpleNavButtonLink.link
})

const buttonTarget = computed(() => {
  return navStore.simpleNavButtonLink.target
})

const scrollToFrom = () => {
  if (!buttonLink.value) {
    scrollTo('#form-section', 20)
  }
}

const {
  navbarStyle,
  isNavbarTransparent,
  isNavbarLight,
  isConfiguredTransparent,
} = useNavbarStyle(
  toRef(props, 'navbarColor'),
  toRef(props, 'isTransparent'),
  toRef(props, 'buttonsColor'),
)
</script>

<template>
  <nav :class="['navi', { 'navi--transparent': isConfiguredTransparent }]">
    <header
      :class="[
        'navigation',
        {
          'navigation--transparent': isConfiguredTransparent,
          'navigation--scrolled':
            isConfiguredTransparent && !isNavbarTransparent,
        },
      ]"
      :style="navbarStyle"
    >
      <div class="navigation__header">
        <div class="navigation__header-content">
          <nuxt-link
            class="navigation-logo"
            :class="{ 'navigation-logo--transparent': isNavbarTransparent }"
            :to="localePath('/')"
          >
            <BaseIconSvg
              :class="[
                'navigation-logo__icon',
                'navigation-logo__icon--mobile',
              ]"
              name="logomark"
            />
            <span class="navigation-logo__desktop">
              <BaseIconSvg
                :class="[
                  'navigation-logo__icon',
                  'navigation-logo__icon--desktop',
                  { 'navigation-logo__icon--hidden': isNavbarLight },
                ]"
                name="logo"
              />
              <BaseIconSvg
                :class="[
                  'navigation-logo__icon',
                  'navigation-logo__icon--desktop',
                  'navigation-logo__icon--overlay',
                  { 'navigation-logo__icon--hidden': !isNavbarLight },
                ]"
                name="infermedica-logo-white"
              />
            </span>
          </nuxt-link>
          <div class="navigation__menu">
            <BaseButton
              id="cta-header"
              class="navigation__cta-button"
              :href="buttonLink || null"
              :target="buttonTarget"
              :title="layoutButtonText"
              small
              @click="scrollToFrom"
            >
              <template #text>
                {{ layoutButtonText }}
              </template>
            </BaseButton>
          </div>
        </div>
      </div>
    </header>
  </nav>
</template>

<style lang="scss" scoped>
@mixin flex-align($gap) {
  display: flex;
  align-items: center;
  gap: $gap;
}

.container {
  @media (min-width: $breakpoint-md) {
    --container-padding: 54px;
  }

  @media (min-width: $breakpoint-lg) {
    --container-padding: 20px;
  }
}

.navigation-logo {
  color: inherit;
  background: inherit;

  :deep(.logomark-plus) {
    transition: opacity 0.35s ease;
  }

  &--transparent {
    :deep(.logomark-plus) {
      opacity: 0;
    }
  }

  &__icon {
    color: inherit;
    display: grid;
    place-items: center;
    transition: opacity 0.3s ease;

    &--mobile {
      width: 32px;
      height: 32px;

      @media (min-width: $breakpoint-md) {
        display: none;
      }
    }

    &--desktop {
      display: none;
      width: 160px;
      height: 32px;
      overflow: visible;

      @media (min-width: $breakpoint-md) {
        display: grid;
      }
    }

    &--overlay {
      position: absolute;
      inset: 0;
    }

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }
  }
}

.navi {
  display: flex;
  height: var(--header-height);

  &--transparent {
    height: 0;
  }
}

.navigation-logo__desktop {
  position: relative;
  display: none;
  width: 160px;
  height: 32px;

  @media (min-width: $breakpoint-md) {
    display: block;
  }
}

.navigation {
  width: 100%;
  top: 0;
  font-size: var(--font-size-paragraph-s);
  position: fixed;
  z-index: 9999;
  background-color: var(--navbar-solid-bg, var(--white));
  color: var(--navbar-solid-text, var(--cobalt-800));
  transition:
    background-color 0.35s ease,
    color 0.35s ease,
    box-shadow 0.35s ease;

  &--transparent:not(&--scrolled) {
    background-color: transparent;
    color: var(--navbar-overlay-text, var(--white));
  }

  &--scrolled {
    @include shadow-flat-soft;
  }

  &__header {
    height: 88px;
    position: absolute;
    width: 100%;
    top: 0;
    background: inherit;
    z-index: 9999;
    display: flex;
    padding: 0 24px;
  }

  &__header-content {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__menu {
    @include flex-align(0);
  }

  &__item {
    @include flex-align(var(--space8));

    cursor: pointer;
  }

  &__icon {
    width: 12px;
    transition: transform 0.2s ease-in-out;

    &--active {
      transform: rotate(180deg);
    }
  }

  &__cta-button {
    margin-left: var(--space16);
  }
}
</style>
